import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type Inspecao, type FotoInspecao, type RespostaInspecao } from "../api";
import ErrorState from "./ErrorState";
import { IconRefresh, IconClose, IconDownload, IconEye, IconSearch } from "../icons";
import { compararNomes } from "../ordem";

/**
 * Relatório de Inspeção de Veículos (gestão).
 *
 * As inspeções são preenchidas pela equipe no app do colaborador (checklist
 * pré-uso, com foto por item) e gravadas na tabela `inspecoes_veiculo` do banco
 * COMPARTILHADO — por isso chegam aqui sozinhas, sem nenhum envio manual. Esta
 * tela é somente leitura: lista, filtra, resume e abre o detalhe com as fotos.
 */

type Aba = "inspecoes" | "pendencias" | "veiculos";

const ABAS: { key: Aba; label: string }[] = [
  { key: "inspecoes", label: "Inspeções" },
  { key: "pendencias", label: "Não conformidades" },
  { key: "veiculos", label: "Por veículo" },
];

const STATUS_PILL: Record<string, string> = {
  Conforme: "pill-ok",
  "Com pendências": "pill-danger",
};

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/**
 * Resposta que caracteriza pendência. Compara sem acento/caixa e pelo começo
 * porque convivem dois vocabulários na mesma tabela: "Não conforme" (registros
 * antigos, feitos por aqui) e "Não conformidade" (o app do colaborador hoje).
 */
function ehNaoConforme(resposta: unknown): boolean {
  return String(resposta ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
    .startsWith("nao conform");
}

/** As respostas vêm em JSONB; blinda contra registro antigo/torto. */
function respostasDe(i: Inspecao): RespostaInspecao[] {
  const r = i.respostas as unknown;
  if (Array.isArray(r)) return r as RespostaInspecao[];
  if (typeof r === "string") {
    try {
      const p = JSON.parse(r);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Quantos itens ficaram como não conformidade nesta inspeção. */
function totalNC(i: Inspecao): number {
  return respostasDe(i).filter((r) => ehNaoConforme(r.resposta)).length;
}

/** Placa — modelo, preferindo o que foi digitado na inspeção. */
function veiculoDe(i: Inspecao): string {
  const placa = i.placa || i.veiculo_placa || "";
  const modelo = i.modelo || i.veiculo_modelo || "";
  return [placa, modelo].filter(Boolean).join(" — ") || "Veículo avulso";
}

function placaDe(i: Inspecao): string {
  return (i.placa || i.veiculo_placa || "").trim() || "Sem placa";
}

function quemDe(i: Inspecao): string {
  return i.colaborador_nome || i.inspetor || "—";
}

/** Baixa um CSV que o Excel em português abre certo (BOM + ponto e vírgula). */
function baixarCsv(nome: string, linhas: (string | number | null | undefined)[][]) {
  const escapar = (v: string | number | null | undefined) => {
    let texto = String(v ?? "");
    // Uma observação digitada começando com = + - @ seria lida como FÓRMULA pelo
    // Excel/LibreOffice ao abrir o arquivo. O apóstrofo neutraliza isso.
    if (/^[=+\-@\t\r]/.test(texto)) texto = `'${texto}`;
    return `"${texto.replace(/"/g, '""')}"`;
  };
  const texto = linhas.map((l) => l.map(escapar).join(";")).join("\r\n");
  const blob = new Blob(["﻿" + texto], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function RelatorioInspecaoView() {
  const [aba, setAba] = useState<Aba>("inspecoes");
  const [itens, setItens] = useState<Inspecao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Filtros (todos opcionais — sem filtro, mostra tudo).
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [placa, setPlaca] = useState("");
  const [pessoa, setPessoa] = useState("");
  const [status, setStatus] = useState("");
  const [busca, setBusca] = useState("");

  const [detalhe, setDetalhe] = useState<Inspecao | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItens(await api.inspecoes.list());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Opções dos selects saem dos próprios dados (não há cadastro de "inspeções"),
  // em ordem alfabética pela regra única do sistema (src/ordem.ts).
  const placas = useMemo(
    () => Array.from(new Set(itens.map(placaDe))).sort(compararNomes),
    [itens],
  );
  const pessoas = useMemo(
    () => Array.from(new Set(itens.map(quemDe).filter((p) => p !== "—"))).sort(compararNomes),
    [itens],
  );

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens.filter((i) => {
      const dia = String(i.data ?? "").slice(0, 10);
      if (de && dia < de) return false;
      if (ate && dia > ate) return false;
      if (placa && placaDe(i) !== placa) return false;
      if (pessoa && quemDe(i) !== pessoa) return false;
      if (status && i.status !== status) return false;
      if (termo) {
        // Procura em TODO o conteúdo da inspeção (inclusive itens conformes) —
        // buscar só nas pendências esconderia inspeções sem o usuário entender
        // por quê.
        const alvo = [
          veiculoDe(i),
          quemDe(i),
          i.inspetor ?? "",
          i.observacoes ?? "",
          i.status,
          respostasDe(i)
            .map((r) => `${r.item} ${r.resposta} ${r.obs ?? ""}`)
            .join(" "),
        ]
          .join(" ")
          .toLowerCase();
        if (!alvo.includes(termo)) return false;
      }
      return true;
    });
  }, [itens, de, ate, placa, pessoa, status, busca]);

  const resumo = useMemo(() => {
    const comPendencia = filtradas.filter((i) => totalNC(i) > 0 || i.status === "Com pendências");
    return {
      total: filtradas.length,
      conformes: filtradas.length - comPendencia.length,
      comPendencia: comPendencia.length,
      itensNC: filtradas.reduce((s, i) => s + totalNC(i), 0),
      veiculos: new Set(filtradas.map(placaDe)).size,
    };
  }, [filtradas]);

  /** Uma linha por item não conforme (é a lista de ação do gestor). */
  const pendencias = useMemo(
    () =>
      filtradas.flatMap((i) =>
        respostasDe(i)
          .map((r, indice) => ({ r, indice }))
          .filter(({ r }) => ehNaoConforme(r.resposta))
          .map(({ r, indice }) => ({ inspecao: i, item: r.item, obs: r.obs ?? "", indice })),
      ),
    [filtradas],
  );

  /** Resumo por veículo: quantas inspeções, quantas com pendência, a última. */
  const porVeiculo = useMemo(() => {
    const mapa = new Map<
      string,
      { placa: string; modelo: string; total: number; comPendencia: number; itensNC: number; ultima: string; km: number | null }
    >();
    for (const i of filtradas) {
      const chave = placaDe(i);
      const atual =
        mapa.get(chave) ??
        { placa: chave, modelo: i.modelo || i.veiculo_modelo || "", total: 0, comPendencia: 0, itensNC: 0, ultima: "", km: null };
      const nc = totalNC(i);
      atual.total += 1;
      if (nc > 0 || i.status === "Com pendências") atual.comPendencia += 1;
      atual.itensNC += nc;
      const dia = String(i.data ?? "").slice(0, 10);
      if (dia > atual.ultima) {
        atual.ultima = dia;
        atual.km = i.km ?? null;
        if (!atual.modelo) atual.modelo = i.modelo || i.veiculo_modelo || "";
      }
      mapa.set(chave, atual);
    }
    // Ranking (mais não conformidades primeiro) — no empate, placa A → Z.
    return Array.from(mapa.values()).sort((a, b) => b.itensNC - a.itensNC || compararNomes(a.placa, b.placa));
  }, [filtradas]);

  const limparFiltros = () => {
    setDe("");
    setAte("");
    setPlaca("");
    setPessoa("");
    setStatus("");
    setBusca("");
  };
  const temFiltro = !!(de || ate || placa || pessoa || status || busca.trim());

  function exportar() {
    const hoje = new Date().toISOString().slice(0, 10);
    if (aba === "pendencias") {
      baixarCsv(`nao-conformidades-${hoje}.csv`, [
        ["Data", "Veículo", "Responsável", "Item", "Observação", "KM"],
        ...pendencias.map((p) => [
          dataBR(p.inspecao.data),
          veiculoDe(p.inspecao),
          quemDe(p.inspecao),
          p.item,
          p.obs,
          p.inspecao.km ?? "",
        ]),
      ]);
      return;
    }
    if (aba === "veiculos") {
      baixarCsv(`inspecoes-por-veiculo-${hoje}.csv`, [
        ["Placa", "Modelo", "Inspeções", "Com pendência", "Itens não conformes", "Última inspeção", "KM"],
        ...porVeiculo.map((v) => [v.placa, v.modelo, v.total, v.comPendencia, v.itensNC, dataBR(v.ultima), v.km ?? ""]),
      ]);
      return;
    }
    baixarCsv(`inspecoes-${hoje}.csv`, [
      ["Data", "Veículo", "Responsável", "KM", "Itens não conformes", "Status", "Fotos", "Observações"],
      ...filtradas.map((i) => [
        dataBR(i.data),
        veiculoDe(i),
        quemDe(i),
        i.km ?? "",
        totalNC(i),
        i.status,
        i.fotos_total ?? 0,
        i.observacoes ?? "",
      ]),
    ]);
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;

  const vazio = aba === "pendencias" ? pendencias.length === 0 : filtradas.length === 0;
  const nada = itens.length === 0;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="almox-tabs">
          {ABAS.map((a) => (
            <button
              key={a.key}
              className={`almox-tab ${aba === a.key ? "active" : ""}`}
              onClick={() => setAba(a.key)}
            >
              {a.label}
              {a.key === "pendencias" && pendencias.length > 0 ? ` (${pendencias.length})` : ""}
            </button>
          ))}
        </div>

        <div className="toolbar-right">
          <button className="btn-secondary" onClick={exportar} disabled={loading || vazio} title="Exportar em CSV">
            <IconDownload size={16} /> Exportar
          </button>
          <button className="btn-secondary" onClick={carregar} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      <div className="insp-filtros">
        <label className="field insp-filtro">
          <span>De</span>
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} />
        </label>
        <label className="field insp-filtro">
          <span>Até</span>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
        </label>
        <label className="field insp-filtro">
          <span>Veículo</span>
          <select value={placa} onChange={(e) => setPlaca(e.target.value)}>
            <option value="">Todos</option>
            {placas.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="field insp-filtro">
          <span>Responsável</span>
          <select value={pessoa} onChange={(e) => setPessoa(e.target.value)}>
            <option value="">Todos</option>
            {pessoas.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="field insp-filtro">
          <span>Situação</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todas</option>
            <option value="Conforme">Conforme</option>
            <option value="Com pendências">Com pendências</option>
          </select>
        </label>
        <label className="field insp-filtro insp-filtro-busca">
          <span>Buscar</span>
          <div className="search-wrap">
            <IconSearch size={16} />
            <input
              className="search"
              placeholder="Placa, responsável, item ou observação…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </label>
        {temFiltro && (
          <button className="btn-secondary" onClick={limparFiltros}>
            Limpar filtros
          </button>
        )}
      </div>

      <div className="cards insp-cards">
        <div className="card">
          <div className="card-body">
            <span className="card-value">{resumo.total}</span>
            <span className="card-label">Inspeções</span>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <span className="card-value tom-ok">{resumo.conformes}</span>
            <span className="card-label">Conformes</span>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <span className="card-value tom-danger">{resumo.comPendencia}</span>
            <span className="card-label">Com pendências</span>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <span className="card-value tom-warn">{resumo.itensNC}</span>
            <span className="card-label">Itens não conformes</span>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <span className="card-value">{resumo.veiculos}</span>
            <span className="card-label">Veículos inspecionados</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : nada ? (
        <div className="empty">
          Nenhuma inspeção registrada ainda. Assim que a equipe preencher a inspeção do veículo no
          app do colaborador, ela aparece aqui automaticamente.
        </div>
      ) : vazio ? (
        <div className="empty">
          {aba === "pendencias"
            ? "Nenhuma não conformidade no filtro escolhido."
            : "Nenhuma inspeção no filtro escolhido."}
        </div>
      ) : aba === "inspecoes" ? (
        <div className="table-wrap">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Data</th>
                <th>Veículo</th>
                <th>Responsável</th>
                <th>KM</th>
                <th>Não conformes</th>
                <th>Fotos</th>
                <th>Situação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((i) => {
                const nc = totalNC(i);
                return (
                  <tr key={i.id} className={nc > 0 ? "is-nc-row" : ""}>
                    <td>{dataBR(i.data)}</td>
                    <td>{veiculoDe(i)}</td>
                    <td>{quemDe(i)}</td>
                    <td>{i.km != null ? i.km.toLocaleString("pt-BR") : "—"}</td>
                    <td>{nc > 0 ? <strong>{nc}</strong> : "—"}</td>
                    <td>{i.fotos_total ? i.fotos_total : "—"}</td>
                    <td>
                      <span className={`pill ${STATUS_PILL[i.status] ?? "pill-neutral"}`}>{i.status}</span>
                    </td>
                    <td className="col-actions">
                      <button className="icon-btn" title="Ver inspeção" onClick={() => setDetalhe(i)}>
                        <IconEye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : aba === "pendencias" ? (
        <div className="table-wrap">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Data</th>
                <th>Veículo</th>
                <th>Responsável</th>
                <th>Item não conforme</th>
                <th>Observação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendencias.map((p, n) => (
                <tr key={`${p.inspecao.id}-${p.indice}-${n}`}>
                  <td>{dataBR(p.inspecao.data)}</td>
                  <td>{veiculoDe(p.inspecao)}</td>
                  <td>{quemDe(p.inspecao)}</td>
                  <td>{p.item}</td>
                  <td>{p.obs || "—"}</td>
                  <td className="col-actions">
                    <button className="icon-btn" title="Ver inspeção" onClick={() => setDetalhe(p.inspecao)}>
                      <IconEye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Modelo</th>
                <th>Inspeções</th>
                <th>Com pendência</th>
                <th>Itens não conformes</th>
                <th>Última inspeção</th>
                <th>KM</th>
              </tr>
            </thead>
            <tbody>
              {porVeiculo.map((v) => (
                <tr key={v.placa} className={v.itensNC > 0 ? "is-nc-row" : ""}>
                  <td>
                    <strong>{v.placa}</strong>
                  </td>
                  <td>{v.modelo || "—"}</td>
                  <td>{v.total}</td>
                  <td>{v.comPendencia}</td>
                  <td>{v.itensNC > 0 ? <strong>{v.itensNC}</strong> : "—"}</td>
                  <td>{dataBR(v.ultima)}</td>
                  <td>{v.km != null ? v.km.toLocaleString("pt-BR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detalhe && (
        <DetalheModal
          inspecao={detalhe}
          onClose={() => setDetalhe(null)}
          onAmpliar={setZoom}
          /* Com a foto ampliada aberta, o Esc fecha só a foto — não os dois. */
          escAtivo={!zoom}
        />
      )}
      {zoom && <FotoZoom url={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

/* ----------------------------- Detalhe da inspeção ---------------------------- */
function DetalheModal({
  inspecao,
  onClose,
  onAmpliar,
  escAtivo,
}: {
  inspecao: Inspecao;
  onClose: () => void;
  onAmpliar: (url: string) => void;
  escAtivo: boolean;
}) {
  const respostas = respostasDe(inspecao);
  const [fotos, setFotos] = useState<FotoInspecao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erroFotos, setErroFotos] = useState<string | null>(null);

  // As imagens não vêm na listagem (seriam megabytes por inspeção): são
  // buscadas aqui, só quando alguém abre o detalhe.
  useEffect(() => {
    if (!inspecao.fotos_total) return;
    let vivo = true;
    setCarregando(true);
    api.inspecoes
      .fotos(inspecao.id)
      .then((f) => {
        if (vivo) setFotos(f);
      })
      .catch((err) => {
        if (vivo) setErroFotos(err instanceof ApiError ? err.message : "Falha ao carregar as fotos.");
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [inspecao.id, inspecao.fotos_total]);

  useEffect(() => {
    if (!escAtivo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escAtivo]);

  /** Fotos de um item, casadas pela posição (e pelo texto, para registros antigos). */
  const fotosDoItem = (indice: number, item: string) =>
    fotos.filter((f) => f.item_index === indice || (f.item && f.item === item));

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-card modal-lg"
        role="dialog"
        aria-modal="true"
        aria-label={`Inspeção de ${veiculoDe(inspecao)}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>Inspeção — {veiculoDe(inspecao)}</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar" title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="inspecao-modal-meta">
            <span>{dataBR(inspecao.data)}</span>
            {inspecao.km != null && <span>KM {inspecao.km.toLocaleString("pt-BR")}</span>}
            <span className={`pill ${STATUS_PILL[inspecao.status] ?? "pill-neutral"}`}>{inspecao.status}</span>
            <span className="muted">por {quemDe(inspecao)}</span>
          </div>
          {carregando && <p className="muted">Carregando fotos…</p>}
          {erroFotos && <div className="form-error">{erroFotos}</div>}

          <table className="table table-compact">
            <thead>
              <tr>
                <th>Item</th>
                <th>Resposta</th>
                <th>Observação</th>
                <th>Fotos</th>
              </tr>
            </thead>
            <tbody>
              {respostas.map((r, i) => {
                const doItem = fotosDoItem(i, r.item);
                return (
                  <tr key={i} className={ehNaoConforme(r.resposta) ? "is-nc-row" : ""}>
                    <td>{r.item}</td>
                    <td>{r.resposta}</td>
                    <td>{r.obs || "—"}</td>
                    <td>
                      {doItem.length === 0 ? (
                        "—"
                      ) : (
                        <ul className="inspecao-foto-lista compacta">
                          {doItem.map((f) => (
                            <li key={f.id} className="inspecao-foto-thumb">
                              <button
                                type="button"
                                className="inspecao-foto-abrir"
                                onClick={() => onAmpliar(f.url)}
                              >
                                <img src={f.url} alt={`Foto de ${r.item}`} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {inspecao.observacoes && (
            <p className="inspecao-modal-obs">
              <strong>Observações gerais:</strong> {inspecao.observacoes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Foto ampliada ------------------------------- */
function FotoZoom({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay foto-zoom" role="dialog" aria-modal="true" aria-label="Foto da inspeção" onMouseDown={onClose}>
      <img src={url} alt="Foto da inspeção" onMouseDown={(e) => e.stopPropagation()} />
      <button
        type="button"
        className="icon-btn foto-zoom-fechar"
        onClick={onClose}
        aria-label="Fechar a foto"
        title="Fechar"
      >
        <IconClose size={20} />
      </button>
    </div>
  );
}
